import { activityFetch as fetch } from '../../components/feedback/requestActivity'
export const environmentName = value => ({ production: '운영', development: '개발', staging: '검증' }[value] || '용도 미지정')
export const runtimeName = value => value === 'k3s' ? 'K3s' : 'Kubernetes'
export const fresh = (value, now = Date.now()) => Number.isFinite(Date.parse(value)) && Math.abs(now - Date.parse(value)) < 180000
const list = value => Array.isArray(value) ? value : []
const unique = (items, key) => [...new Map(items.map(item => [key(item), item])).values()]
const same = (a, b) => Boolean(a && b && a.toLowerCase() === b.toLowerCase())

// A node is a role of a server, not another billable server. Never join by
// provider/environment alone, and never invent membership for an ambiguous name.
export function assetModel(data) {
  const servers = unique(list(data.fleet?.hosts), host => host.address || host.id).map(host => ({ ...host, nodes: [] }))
  const clusters = unique(list(data.clusters?.clusters), cluster => cluster.id).map(cluster => ({
    ...cluster, snapshot: data['cluster:' + cluster.id],
  }))
  const unmatched = []
  const pods = []
  for (const cluster of clusters) {
    if (!cluster.snapshot) continue
    const clusterPods = unique(cluster.snapshot.pods, pod => `${pod.namespace}/${pod.name}`)
    pods.push(...clusterPods)
    for (const node of unique(cluster.snapshot.nodes, node => node.uid || node.name)) {
      const entry = { ...node, cluster, pods: clusterPods.filter(pod => pod.node === node.name) }
      const candidates = servers.filter(host => (!host.provider || same(host.provider, cluster.provider))
        && (same(node.internalIP, host.internalIP) || same(node.internalIP, host.address) || same(node.name, host.hostname)))
      if (candidates.length === 1) candidates[0].nodes.push(entry)
      else unmatched.push(entry)
    }
    const unassigned = clusterPods.filter(pod => !cluster.snapshot.nodes.some(node => node.name === pod.node))
    if (unassigned.length) unmatched.push({ name: '서버 배치 미확인', cluster, pods: unassigned, ready: 'Unknown' })
  }
  const hosts = ['docker', 'podman'].filter(key => data[key]).map(key => ({
    key, label: key === 'docker' ? 'Docker' : 'Podman', snapshot: data[key],
    containers: unique(data[key].containers, c => c.id || c.name),
  }))
  const available = clusters.filter(c => c.snapshot).length
  const clusterComplete = Boolean(data.clusters) && available === clusters.length
  const containerSources = available + hosts.length
  const containers = pods.reduce((n, pod) => n + pod.containers, 0) + hosts.reduce((n, host) => n + host.containers.length, 0)
  return { servers, clusters, unmatched, hosts, pods,
    registered: data.fleet ? servers.filter(host => host.registered).length : null,
    podCount: available || (data.clusters && !clusters.length) ? pods.length : null,
    containerCount: containerSources ? containers : null,
    podPartial: !clusterComplete,
    containerPartial: !clusterComplete || hosts.length < 2 || hosts.some(h => h.snapshot.truncated || h.snapshot.containersTruncated),
    clusterAvailable: available,
  }
}

const SOURCES = [
  ['fleet', '서버 등록', '/api/orch/k8s/inventory', d => d.readOnly === true && Array.isArray(d.hosts)],
  ['clusters', '클러스터 목록', '/api/orch/k8s/clusters', d => Array.isArray(d.clusters)],
  ['docker', 'Docker', '/api/docker/snapshot', d => d.readOnly === true && d.source === 'api-host-docker' && Array.isArray(d.containers)],
  ['podman', 'Podman', '/api/host-images/snapshot', d => d.readOnly === true && d.source === 'api-host-podman' && Array.isArray(d.containers)],
  ['registry', '공유 이미지 저장소', '/api/registry/inventory/snapshot', d => d.readOnly === true && d.source === 'managed-registry' && Array.isArray(d.repositories)],
  ['processes', 'PM2', '/api/processes/snapshot', d => d.readOnly === true && Array.isArray(d.hosts)],
]

export async function loadAssets(signal) {
  const data = {}, errors = [], sources = []
  const token = sessionStorage.getItem('token') || localStorage.getItem('token')
  async function read([key, label, url, validate]) {
    try {
      const response = await fetch(url, { credentials: 'include', cache: 'no-store', signal,
        headers: token ? { Authorization: `Bearer ${token}` } : {} })
      if (response.status === 401) throw Error('로그인을 다시 확인해 주세요.')
      if (response.status === 403) throw Error('관리자 권한이 필요합니다.')
      if (!response.ok) throw Error('연결 또는 조회 권한을 확인해 주세요.')
      const value = await response.json()
      if (!validate(value)) throw Error('응답 형식을 확인할 수 없습니다.')
      if (key !== 'clusters' && !fresh(value.observedAt || value.checkedAt)) throw Error('최근 조회값이 없습니다.')
      data[key] = value
      sources.push({ key, label, at: value.observedAt || value.checkedAt })
    } catch (error) {
      if (signal.aborted) throw error
      errors.push({ key, label, message: error.message })
    }
  }
  await Promise.all(SOURCES.map(read))
  await Promise.all(unique(list(data.clusters?.clusters), cluster => cluster.id).map(cluster => read([
    'cluster:' + cluster.id, cluster.name,
    '/api/orch/k8s/snapshot?cluster=' + encodeURIComponent(cluster.id),
    d => d.readOnly === true && d.clusterId === cluster.id && d.namespace === cluster.namespace
      && Array.isArray(d.nodes) && Array.isArray(d.pods)
      && d.pods.every(p => Number.isSafeInteger(p.containers) && p.containers >= 0),
  ])))
  return { data, errors, sources }
}
